import type { UseFetchStateProps } from "./useFetch";
import type { PickRename } from "../types/global";

import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import { concatParams } from "../utils";
import { useFetch } from "./useFetch";
import { useSite } from "./useSite";

type Response<Areas extends string> = Record<
	Areas,
	{ interest: { id?: string; label: string }; weight: number }
>;

type State<Areas extends string> = PickRename<
	UseFetchStateProps<Response<Areas>>,
	"data",
	"query"
>;

type SetState<Areas extends string> = { areas: Array<Areas> };

type Return<Areas extends string> = [
	State<Areas>,
	(setQuery: SetState<Areas>) => void,
];

/**
 * Hook to receive GPX interests from Griddo's Public API.
 *
 * @example
 * const [{ query }, setQuery] = useReceiveInterests();
 * setQuery({ areas: ["Area1"] });
 */
export function useReceiveInterests<Areas extends string>(): Return<Areas> {
	const { publicApiUrl } = useSite();
	const [url, setUrl] = React.useState<string>();

	const [id] = React.useState(() => {
		if (typeof window === "undefined") return "";
		try {
			let storedId = localStorage.getItem("gpx_id");
			if (!storedId) {
				storedId = uuidv4();
				localStorage.setItem("gpx_id", storedId);
			}
			return storedId;
		} catch (error) {
			console.error("Error accessing localStorage:", error);
			return uuidv4();
		}
	});

	const setQuery = ({ areas }: { areas: Array<Areas> }) => {
		const urlWithParams = concatParams({
			baseUrl: `${publicApiUrl}/gpx/profile/`,
			params: {
				areas,
				id,
			},
		});

		setUrl(urlWithParams);
	};

	const { isError, isLoading, msg, data, isFirstFetch } =
		useFetch<Response<Areas>>(url);

	return [{ query: data, isLoading, isError, msg, isFirstFetch }, setQuery];
}
